import {
  Card,
  Grid,
  KeyValuePair,
  RegularBreakpoints,
  Section,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@cmg/design-system';
import React from 'react';

import { OfferingProfile_ListQuery } from '../../../graphql/__generated__/OfferingProfile';
import LabelWithTooltip from '../components/LabelWithTooltip';
import { useGetCornerstoneSectionData } from '../hooks/useGetCornerstoneSectionData';
import { cardHeight } from '../OfferingProfileCardItem';
import { CornerstoneInvestmentSection } from '../types';
import {
  combinedInvestmentsRowStyle,
  combinedRowStyle,
  StyledTableCell,
  StyledTableHeader,
  totalRowStyle,
} from './CornerstoneInvestorsWidget.styles';

export type CornerstoneTableProps = {
  investments: CornerstoneInvestmentSection;
  investmentType: string;
  headerTooltipText: string;
};
export type Props = {
  offeringProfile: OfferingProfile_ListQuery;
  cardSizes?: RegularBreakpoints;
  id?: string;
};

function parseAmount(amountStr) {
  const match = amountStr.match(/[\d.]+/); // Match the numeric part
  return match ? parseFloat(match[0]) : NaN; // Convert to number, or NaN if invalid
}

const CornerstoneTable: React.FC<CornerstoneTableProps> = ({
  investments,
  investmentType,
  headerTooltipText,
}) => {
  const {
    investorsCount,
    investmentAmount,
    individualInvestments,
    combinedAmount,
    combinedInvestments,
  } = investments;

  let hasInsertedCombinedInvestment = false;

  const renderCombinedInvestments = () => (
    <React.Fragment>
      <TableRow key={`Combined ${investmentType}`} sx={combinedRowStyle}>
        <StyledTableCell width="49%" colSpan={3}>
          <Typography variant="highlight2" paddingLeft={1}>
            Combined Amount
          </Typography>
        </StyledTableCell>
        <StyledTableCell align="right" width="17%">
          <Typography variant="highlight2" paddingRight={1}>
            {combinedAmount}
          </Typography>
        </StyledTableCell>
      </TableRow>

      {combinedInvestments.map(({ name, type, isNewInvestor }) => (
        <TableRow key={name} sx={combinedInvestmentsRowStyle}>
          <StyledTableCell width="49%">{name}</StyledTableCell>
          <StyledTableCell width="20%">{type}</StyledTableCell>
          <StyledTableCell align="center" width="14%">
            {isNewInvestor}
          </StyledTableCell>
          <StyledTableCell align="right" width="17%"></StyledTableCell>
        </TableRow>
      ))}

      <TableRow>
        <TableCell padding="checkbox" colSpan={4} divider></TableCell>
      </TableRow>
    </React.Fragment>
  );

  return (
    <Grid item minWidth={theme => theme.spacing(64)} flex={1}>
      <LabelWithTooltip
        label={<Typography variant="highlight1">{`Cornerstone\u2013${investmentType}`}</Typography>}
        tooltipText={headerTooltipText}
        maxWidth={26}
        tooltipPlacement="top-start"
      />
      <Table size="medium">
        <TableHead>
          <TableRow color="text.secondary">
            <TableCell>Investor</TableCell>
            <TableCell>Type</TableCell>
            <StyledTableHeader align="center">Existing Investor</StyledTableHeader>
            <StyledTableHeader align="right">Amount</StyledTableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow key={`Total ${investmentType}`} sx={totalRowStyle}>
            <StyledTableCell width="49%" colSpan={3}>
              <Typography variant="highlight1">{`Total ${investorsCount}`}</Typography>
            </StyledTableCell>
            <StyledTableCell align="right" width="17%">
              <Typography variant="highlight1">{investmentAmount}</Typography>
            </StyledTableCell>
          </TableRow>
          {individualInvestments.map(({ name, type, isNewInvestor, amount, isUndisclosed }) => {
            // Convert strings to numbers
            const numAmount = parseAmount(amount);
            const numCombinedAmount = parseAmount(combinedAmount);

            // Check if we should insert the combined investment row
            const shouldInsertCombinedInvestment =
              !hasInsertedCombinedInvestment &&
              combinedInvestments.length > 0 &&
              !isNaN(numAmount) &&
              numAmount <= numCombinedAmount;

            // If we should insert the combined investment row, mark it as inserted
            if (shouldInsertCombinedInvestment) {
              hasInsertedCombinedInvestment = true;
            }

            return (
              <React.Fragment>
                {shouldInsertCombinedInvestment && renderCombinedInvestments()}

                <TableRow key={name}>
                  <StyledTableCell width="49%">
                    <Typography color={isUndisclosed ? 'text.secondary' : 'text.primary'}>
                      {isUndisclosed ? 'Not Disclosed' : name}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell width="20%">{type}</StyledTableCell>
                  <StyledTableCell align="center" width="14%">
                    {isNewInvestor}
                  </StyledTableCell>
                  <StyledTableCell align="right" width="17%">
                    {amount}
                  </StyledTableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
          {!hasInsertedCombinedInvestment &&
            combinedInvestments.length > 0 &&
            renderCombinedInvestments()}
        </TableBody>
      </Table>
    </Grid>
  );
};

const CornerstoneInvestorsWidget: React.FC<Props> = ({ offeringProfile, cardSizes, id }) => {
  const { offeringParticipants, privatePlacement, totalInvestorsCount, totalInvestmentAmount } =
    useGetCornerstoneSectionData(offeringProfile);
  const title = React.useMemo(
    () => `Cornerstone Investors (${totalInvestorsCount ?? 0})`,
    [totalInvestorsCount]
  );
  return (
    <Grid item {...cardSizes}>
      <Card elevation={0} sx={cardHeight} id={id}>
        <Section title={title} aria-label={title} paddingTop={theme => theme.spacing(3)}>
          {!totalInvestorsCount || totalInvestorsCount === 0 ? (
            <Typography color="text.secondary">No Data Available</Typography>
          ) : (
            <Stack gap={2}>
              <KeyValuePair
                minWidth="large"
                label="Cornerstone Investment Total"
                value={totalInvestmentAmount}
              />
              <TableContainer>
                <Grid container spacing={3} display="flex">
                  {offeringParticipants.investorsCount > 0 && (
                    <CornerstoneTable
                      investments={offeringParticipants}
                      investmentType="Offering Participants"
                      headerTooltipText="Offering Participant Cornerstone investments are included in deal metrics and Gross Proceeds calculations"
                    />
                  )}
                  {privatePlacement.investorsCount > 0 && (
                    <CornerstoneTable
                      investments={privatePlacement}
                      investmentType="Private Placement"
                      headerTooltipText="Private Placement Cornerstone investments are in addition to the offering and not included in Gross Proceeds calculations"
                    />
                  )}
                </Grid>
              </TableContainer>
            </Stack>
          )}
        </Section>
      </Card>
    </Grid>
  );
};

export default CornerstoneInvestorsWidget;
